import {
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import { translateText } from "../../../../utils/translationService";
import { handleOpenSnackBar } from "../../../../utils";
import { useState } from "react";
import { useSelector } from "react-redux";
import CustomDropDown from "../../../../components/CutomDropDown";
import globalStyle from "../../../../globalStyle";
import { PRODUCT_USAGE_TYPE } from "../../../../constants/Variables";

const BasicInformation = ({
  inputValues,
  setInputValues,
  allCategories,
  allSubCategories,
  allSubSubCategories,
  inputError,
  setInputError,
  fetchchedAllSubCategories,
  fetchchedAllSubSubCategories,
  allBrands,
}) => {
  // Loading State
  const [isLoading, setIsLoading] = useState({
    title: false,
  });
  // Get current language
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const handleChangeValues = (event) => {
    const { name, value } = event.target;

    if (name === "category" && value) {
      fetchchedAllSubCategories(value);
      setInputValues((prev) => ({ ...prev, subCategory: "" }));
    }

    if (name === "subCategory" && value && inputValues.category) {
      fetchchedAllSubSubCategories(inputValues.category, value);
      setInputValues((prev) => ({ ...prev, subSubCategory: "" }));
    }

    if (name === "quantity") {
      if (Number(value) < 0) return;
    }

    setInputError((prev) => ({
      ...prev,
      [name]: "",
      inValidQuantity: name === "quantity" ? "" : inputError.quantity,
    }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleTranslateToEnglish = async () => {
    if (!inputValues.name_ro) {
      handleOpenSnackBar(
        "error",
        t("validation.productTitleIsRequiredInRomanian"),
      );
      return;
    }
    setIsLoading((prev) => ({ ...prev, title: true }));
    try {
      const [titleTranslation] = await Promise.all([
        translateText(inputValues.name_ro),
      ]);
      setInputValues((prev) => ({ ...prev, name_en: titleTranslation }));
      setInputError((prev) => ({ ...prev, name_en: "" }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.convertingToEnglishError"));
    } finally {
      setIsLoading((prev) => ({ ...prev, title: false }));
    }
  };

  return (
    <>
      <Grid item md={12} mt={2}>
        <Typography fontWeight={600} fontSize={20} textTransform={"capitalize"}>
          {t("supplierProductTab.basicInformation")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Label
          required
          labelText={`${t("supplierProductTab.productTitleRo")}`}
          htmlFor={`product-name-ro`}
        />
        <TextInput
          id={`product-name-ro`}
          placeholder={`${t("supplierProductTab.enterProductTitleRo")} `}
          required
          type="text"
          name={`name_ro`}
          value={inputValues.name_ro}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.name_ro)}
          helperText={t(inputError.name_ro)}
          InputEndIcon={
            <Typography variant="body2" color="text.secondary">
              {inputValues.name_ro.length}/250
            </Typography>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          mb={"5px"}
        >
          <Label
            required
            labelText={`${t("supplierProductTab.productTitleEn")}`}
            htmlFor={`product-name-en`}
            labelProps={{
              marginBottom: 0,
            }}
          />
          <Chip
            size="small"
            label={t("common.translateToEnglish")}
            onClick={handleTranslateToEnglish}
            disabled={isLoading.title}
            icon={
              isLoading.title ? (
                <CircularProgress size={16} color="primary" />
              ) : null
            }
          />
        </Stack>
        <TextInput
          id={`product-name-en`}
          placeholder={`${t("supplierProductTab.enterProductTitleEn")} `}
          required
          type="text"
          name={`name_en`}
          value={inputValues.name_en}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.name_en)}
          helperText={t(inputError.name_en)}
          InputEndIcon={
            <Typography variant="body2" color="text.secondary">
              {inputValues.name_en.length}/250
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("supplierProductTab.selectCategory")}
          required
          htmlFor={"product-category"}
        />
        <CustomDropDown
          id={"product-category"}
          name={"category"}
          value={inputValues.category}
          onChange={handleChangeValues}
          selectError={Boolean(inputError.category)}
          helperText={t(inputError.category)}
          sx={{
            "& .MuiSelect-select": {
              textTransform: "capitalize",
            },
          }}
        >
          <MenuItem value="" disabled>
            {t("supplierProductTab.selectCategory")}
          </MenuItem>
          {allCategories.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.name[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("supplierProductTab.selectSubCategory")}
          required
          htmlFor={"product-sub-category"}
        />
        <CustomDropDown
          id={"product-sub-category"}
          name={"subCategory"}
          value={inputValues.subCategory}
          onChange={handleChangeValues}
          selectError={Boolean(inputError.subCategory)}
          helperText={t(inputError.subCategory)}
          disabled={!inputValues.category}
          sx={{
            "& .MuiSelect-select": {
              textTransform: "capitalize",
            },
          }}
        >
          <MenuItem value="" disabled>
            {t("supplierProductTab.selectSubCategory")}
          </MenuItem>
          {allSubCategories.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.name[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("supplierProductTab.selectSubSubCategory")}
          required
          htmlFor={"product-sub-sub-category"}
        />
        <CustomDropDown
          id={"product-sub-sub-category"}
          name={"subSubCategory"}
          value={inputValues.subSubCategory}
          onChange={handleChangeValues}
          selectError={Boolean(inputError.subSubCategory)}
          helperText={t(inputError.subSubCategory)}
          disabled={!inputValues.subCategory}
          sx={{
            "& .MuiSelect-select": {
              textTransform: "capitalize",
            },
          }}
        >
          <MenuItem value="" disabled>
            {t("supplierProductTab.selectSubSubCategory")}
          </MenuItem>
          {allSubSubCategories?.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.name[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("supplierProductTab.selectBrand")}
          htmlFor={"product-brand"}
          optional
        />
        <CustomDropDown
          id={"product-brand"}
          name={"brand"}
          value={inputValues.brand}
          onChange={handleChangeValues}
          selectError={Boolean(inputError.brand)}
          helperText={t(inputError.brand)}
          sx={{
            "& .MuiSelect-select": {
              textTransform: "capitalize",
            },
          }}
        >
          <MenuItem value="" disabled>
            {t("supplierProductTab.selectBrand")}
          </MenuItem>

          {allBrands.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.title[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("supplierProductTab.solutionType")}
          required
          htmlFor={`product-solution-type`}
        />

        <CustomDropDown
          name="solutionType"
          value={inputValues.solutionType}
          onChange={handleChangeValues}
          selectError={Boolean(inputError.solutionType)}
          helperText={t(inputError.solutionType)}
          multiple
        >
          <MenuItem value="" disabled>
            {t("supplierProductTab.selectSolutionType")}
          </MenuItem>
          {PRODUCT_USAGE_TYPE.map((item, index) => (
            <MenuItem key={index} value={item.value}>
              {item.label[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>
    </>
  );
};

export default BasicInformation;
